import React, { useState, useRef, useEffect } from 'react'; // ✅ add useRef
import { useSIP } from '../../context/SIPContext';
import IncomingCallPopup from '../../Popups/WebRtc/IncomingCallPopup';
import ActiveCallPopup from '../../Popups/WebRtc/ActiveCallPopup';
import { useDraggable } from '../../hooks/useDraggable';
import Popup from 'reactjs-popup';
import CallFeedback from '../Feedbacks/Feedback';
import useFetch from '../../hooks/useFetch';
import useAuth from '../../hooks/useAuth';
import { FEEDBACK_DETAILS } from '../../utils/ApiUrls';
import DraggablePopup from './DraggablePopup';

// ✅ Keep rest of your component exactly the same
const WebRtcIncomingCallHandler = () => {
    const {
        callStatus, caller, answer, reject, hangup, hold, unhold, mute, unmute, isHeld, isMuted,
        isDialerOpen, closeDialer, makeCall
    } = useSIP();
    const { auth } = useAuth();
    const fetchData = useFetch();
    const [showFeedback, setShowFeedback] = useState(false);
    const [customer, setCustomer] = useState({});
    const [campData, setCampData] = useState({});
    const [fbdata, setFbdata] = useState({
        id: "0",
        uid: "",
        agent_id: "",
        customer_id: "",
        answers: {},
        fbanswer: {},
        comment: "",
        folloup: "",
        dnc: false,
    });

    const feedbackRequestRef = useRef(0);
    const customerFetchedForRef = useRef(null); // caller value already looked up, to avoid refetching

    const fetchCustomerDetails = async () => {
        const requestId = ++feedbackRequestRef.current;
        try {
            const payload = {
                camp_id: localStorage.getItem("camp_id"),
                phone: caller,
                admin_id: auth?.user_details?.parent,
            };
            const res = await fetchData("post", payload, FEEDBACK_DETAILS);

            if (feedbackRequestRef.current !== requestId) return null; // call ended/changed while fetching - discard

            setCustomer(res || {});
            setCampData(res?.campaign_details || {});
            customerFetchedForRef.current = caller;
            return res;
        } catch (err) {
            console.error("Failed to fetch customer details", err);
            return null;
        }
    };

    // Resolve the real customer number as soon as the call connects, since for
    // campaign calls the SIP caller identity can be the campaign's DID rather
    // than the customer's actual number (needed to pre-fill call transfer).
    useEffect(() => {
        if ((callStatus === 'calling' || callStatus === 'active') && caller && customerFetchedForRef.current !== caller) {
            fetchCustomerDetails();
        }
        if (callStatus === 'idle') {
            customerFetchedForRef.current = null;
            setCustomer({});
            setCampData({});
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [callStatus, caller]);

    const openFeedback = async () => {
        const res = customerFetchedForRef.current === caller ? customer : await fetchCustomerDetails();
        if (!res) return;

        setFbdata({
            id: "0",
            uid: res?.uid || "",
            agent_id: "",
            customer_id: res?.id || "",
            answers: {},
            fbanswer: {},
            comment: "",
            folloup: "",
            dnc: false,
        });
        setShowFeedback(true);
    };

    const closeFeedback = (value) => {
        setShowFeedback(value);
    };

    const feedbackPopup = (
        <Popup
            open={showFeedback}
            modal
            nested
            position="center center"
            contentStyle={{ width: "0px", marginTop: "-10px" }}
        >
            {(close) => (
                <CallFeedback
                    setfback={closeFeedback}
                    close={close}
                    customer={customer}
                    call_id={fbdata.uid}
                    camp_data={campData}
                    FBdata={fbdata}
                    setFBdata={setFbdata}
                />
            )}
        </Popup>
    );

    let callUi = null;

    if (callStatus === 'incoming') {
        callUi = (
            <IncomingCallPopup
                caller={caller}
                onAnswer={answer}
                onReject={reject}
            />
        );
    }

    // Single popup for the whole outbound flow: dialing (idle, opened from the
    // header dialpad icon), calling, and active — never more than one on screen.
    const showActiveCallPopup =
        callStatus === 'calling' || callStatus === 'active' || (callStatus === 'idle' && isDialerOpen);

    if (showActiveCallPopup) {
        const popupWidth = 420; // w-[420px]
        const popupHeight = 480; // approx card height
        return (
            <>
                <DraggablePopup
                    initialPos={{
                        x: (window.innerWidth - popupWidth) / 2,
                        y: (window.innerHeight - popupHeight) / 2,
                    }}
                >
                    <ActiveCallPopup
                        caller={caller}
                        customerNumber={customer?.customer_phone_number}
                        callStatus={callStatus}
                        onCall={makeCall}
                        onClose={closeDialer}
                        onHangup={hangup}
                        onHold={hold}
                        onUnhold={unhold}
                        onMute={mute}
                        onUnmute={unmute}
                        isHeld={isHeld}
                        isMuted={isMuted}
                        onFeedback={openFeedback}
                    />
                </DraggablePopup>
                {feedbackPopup}
            </>
        );
    }

    return (
        <>
            {callUi}
            {feedbackPopup}
        </>
    );
};

export default WebRtcIncomingCallHandler;